import * as React from "react";
import { motion, useInView } from "motion/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const VolatilityIcon = ({ impact }) => {
  const barCount = impact === "high" ? 3 : impact === "medium" ? 2 : 1;
  return (
    <div className="flex items-end gap-0.5 h-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <span
          key={i}
          className={cn(
            "w-1 rounded-full",
            i === 0 ? "h-2" : i === 1 ? "h-3" : "h-4",
            i < barCount
              ? impact === "high" ? "bg-red-400" : impact === "medium" ? "bg-amber-400" : "bg-zinc-400"
              : "bg-zinc-700"
          )}
        />
      ))}
    </div>
  );
};

const FLAG_CODES = {
  US: "us", EU: "eu", GB: "gb", JP: "jp", CA: "ca", AU: "au",
  CN: "cn", IN: "in", DE: "de", FR: "fr", CH: "ch", KR: "kr",
  BR: "br", MX: "mx", NZ: "nz", SG: "sg", HK: "hk",
};

export const EconomicCalendar = React.forwardRef(
  ({ title, events, className, onEventClick }, ref) => {
    const scrollRef = React.useRef(null);
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(true);
    const containerRef = React.useRef(null);
    const inView = useInView(containerRef, { once: true, margin: "-40px" });

    const handleScroll = React.useCallback(() => {
      const c = scrollRef.current;
      if (!c) return;
      setCanScrollLeft(c.scrollLeft > 0);
      setCanScrollRight(c.scrollLeft < c.scrollWidth - c.clientWidth - 1);
    }, []);

    const scroll = (dir) => {
      const c = scrollRef.current;
      if (!c) return;
      c.scrollBy({ left: dir === "left" ? -c.clientWidth * 0.8 : c.clientWidth * 0.8, behavior: "smooth" });
    };

    React.useEffect(() => {
      const c = scrollRef.current;
      if (!c) return;
      c.addEventListener("scroll", handleScroll);
      handleScroll();
      return () => c.removeEventListener("scroll", handleScroll);
    }, [events, handleScroll]);

    const groupedByDate = React.useMemo(() => {
      const groups = {};
      events.forEach((e) => {
        const d = e.date || "Unknown";
        if (!groups[d]) groups[d] = [];
        groups[d].push(e);
      });
      return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    }, [events]);

    const formatDate = (dateStr) => {
      try {
        const d = new Date(dateStr + "T00:00:00");
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diff = Math.floor((d - today) / 86400000);
        if (diff === 0) return "Today";
        if (diff === 1) return "Tomorrow";
        return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
      } catch {
        return dateStr;
      }
    };

    return (
      <div ref={ref} className={cn("w-full", className)} data-testid="economic-calendar">
        <div ref={containerRef}>
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-sm font-mono uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-500 flex items-center gap-2">
              {title}
            </h2>
            <div className="flex items-center gap-1.5">
              {canScrollLeft && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={() => scroll("left")}
                  aria-label="Scroll left"
                  className="p-1 border border-zinc-200 dark:border-zinc-700 text-zinc-500 hover:text-zinc-200 hover:border-zinc-500 transition-colors"
                  data-testid="calendar-scroll-left"
                >
                  <ChevronLeft className="h-4 w-4" />
                </motion.button>
              )}
              {canScrollRight && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={() => scroll("right")}
                  aria-label="Scroll right"
                  className="p-1 border border-zinc-200 dark:border-zinc-700 text-zinc-500 hover:text-zinc-200 hover:border-zinc-500 transition-colors"
                  data-testid="calendar-scroll-right"
                >
                  <ChevronRight className="h-4 w-4" />
                </motion.button>
              )}
            </div>
          </div>

          {/* Scrollable container */}
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-4 scroll-smooth"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {events.length === 0 ? (
              <div className="w-full py-12 text-center">
                <p className="text-sm font-mono text-zinc-500 dark:text-zinc-600">No upcoming events</p>
              </div>
            ) : (
              groupedByDate.map(([date, dateEvents], gi) => (
                <div key={date} className="flex-shrink-0">
                  <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-zinc-400 dark:text-zinc-600 mb-2 px-1">
                    {formatDate(date)}
                  </p>
                  <div className="flex gap-3">
                    {dateEvents.map((event, i) => (
                      <motion.div
                        key={`${date}-${i}`}
                        initial={inView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
                        animate={inView ? { y: 0, opacity: 1 } : {}}
                        transition={{ delay: gi * 0.1 + i * 0.05, type: "spring", stiffness: 100, damping: 14 }}
                        onClick={() => onEventClick && event.ticker && onEventClick(event.ticker)}
                        className={cn(
                          "flex-shrink-0 w-64 border p-4 transition-all duration-200",
                          "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50",
                          "hover:border-zinc-300 dark:hover:border-zinc-600 hover:shadow-sm",
                          event.ticker && onEventClick && "cursor-pointer"
                        )}
                      >
                        {/* Top row: time + volatility */}
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              "text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded",
                              event.impact === "high" ? "bg-red-500/10 text-red-400" :
                              event.impact === "medium" ? "bg-amber-500/10 text-amber-400" :
                              "bg-zinc-500/10 text-zinc-400"
                            )}>
                              {event.time}
                            </span>
                            {event.type === "earnings" && (
                              <span className="text-[9px] font-mono tracking-wider uppercase text-violet-400 bg-violet-500/10 px-1.5 py-0.5 rounded">
                                Earnings
                              </span>
                            )}
                            {event.type === "economic" && (
                              <span className="text-[9px] font-mono tracking-wider uppercase text-sky-500 dark:text-sky-400 bg-sky-500/10 px-1.5 py-0.5 rounded">
                                Macro
                              </span>
                            )}
                          </div>
                          <VolatilityIcon impact={event.impact} />
                        </div>

                        {/* Country flag + event name */}
                        <div className="flex items-center gap-2.5 mb-2">
                          <img
                            src={`https://flagcdn.com/w40/${(FLAG_CODES[event.countryCode] || event.countryCode || "us").toLowerCase()}.png`}
                            alt={event.countryCode}
                            className="h-6 w-6 rounded-full object-cover bg-zinc-200 dark:bg-zinc-800 shrink-0"
                          />
                          <h3 className="text-xs font-semibold text-zinc-800 dark:text-zinc-200 truncate leading-snug">
                            {event.eventName}
                          </h3>
                        </div>

                        {/* Compliance badge */}
                        {event.verdict && (
                          <div className="mb-3">
                            <span className={cn(
                              "text-[8px] font-mono font-bold uppercase tracking-widest px-1.5 py-0.5 border",
                              event.verdict === "compliant" ? "bg-green-50 text-green-700 border-green-300 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800" :
                              event.verdict === "borderline" ? "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800" :
                              "bg-red-50 text-red-700 border-red-300 dark:bg-red-950/30 dark:text-red-400 dark:border-red-800"
                            )}>
                              {event.verdict === "compliant" ? "Halal" : event.verdict === "borderline" ? "Borderline" : "Non-Compliant"}
                            </span>
                          </div>
                        )}

                        {/* Data row: Actual / Forecast / Prior */}
                        <div className="grid grid-cols-3 text-center border-t border-zinc-100 dark:border-zinc-800 pt-3">
                          <div>
                            <p className="text-[9px] font-mono text-zinc-400 dark:text-zinc-600 uppercase">Actual</p>
                            <p className="text-xs font-mono font-medium text-zinc-800 dark:text-zinc-200 mt-0.5">{event.actual ?? "—"}</p>
                          </div>
                          <div>
                            <p className="text-[9px] font-mono text-zinc-400 dark:text-zinc-600 uppercase">Forecast</p>
                            <p className="text-xs font-mono font-medium text-zinc-800 dark:text-zinc-200 mt-0.5">{event.forecast ?? "—"}</p>
                          </div>
                          <div>
                            <p className="text-[9px] font-mono text-zinc-400 dark:text-zinc-600 uppercase">Prior</p>
                            <p className="text-xs font-mono font-medium text-zinc-800 dark:text-zinc-200 mt-0.5">{event.prior ?? "—"}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }
);

EconomicCalendar.displayName = "EconomicCalendar";
